import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { formatMinutes } from '@jupi/shared'
import { RemoteContent, Title } from '@jupi/ui'
import { useFlightLogAircraft, useFlightLogs } from '../../api/flightLog'
import { FlightLogCard } from '../../components/flightLog/FlightLogCard'
import { TimeBaseToggle } from '../../components/flightLog/timeBase'

// The flight log: newest first, one card per flight. The filters live in
// the URL so a reload or a shared link keeps them. Logging a flight is the
// Fleet area's floating button (config/areas.ts).
export default function FlightLogPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const registration = params.get('registration') ?? ''
  const mine = params.get('mine') === 'true'
  const page = Number(params.get('page')) || 1

  const { data, isLoading, error } = useFlightLogs({ registration: registration || undefined, mine, page })
  const { data: aircraft = [] } = useFlightLogAircraft()
  const selected = aircraft.find((a) => a.registration === registration)

  // Changing a filter changes the result set, so it drops the page.
  const setFilter = (next: { registration?: string; mine?: boolean; page?: number }) => {
    const p = new URLSearchParams()
    const reg = next.registration ?? registration
    if (reg) p.set('registration', reg)
    if (next.mine ?? mine) p.set('mine', 'true')
    if (next.page && next.page > 1) p.set('page', String(next.page))
    setParams(p)
  }

  return (
    <Box>
      <Title label={t('flightLog.title')} />
      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select
          size="small"
          label={t('booking.aircraft')}
          value={registration}
          onChange={(e) => setFilter({ registration: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('flightLog.allAircraft')}</MenuItem>
          {aircraft.map((a) => (
            <MenuItem key={a.registration} value={a.registration}>
              {a.registration}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={mine} onChange={(e) => setFilter({ mine: e.target.checked })} />}
          label={t('flightLog.mineOnly')}
        />
        <TimeBaseToggle />
        {selected?.current ? (
          <Typography variant="body2" color="text.secondary">
            {t('flightLog.currentTotal', {
              total: formatMinutes(selected.current.totalMinutes),
              landings: selected.current.totalLandings,
              book: selected.current.bookNumber,
            })}
          </Typography>
        ) : null}
      </Stack>

      <RemoteContent isLoading={isLoading} error={error}>
        {data && data.flights.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('flightLog.none')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {data?.flights.map((flight) => (
              <FlightLogCard key={flight.flightId} flight={flight} to={`/fleet/logs/${flight.flightId}`} />
            ))}
          </Stack>
        )}
      </RemoteContent>

      {data && data.pages > 1 ? (
        <Pagination
          count={data.pages}
          page={page}
          onChange={(_, next) => setFilter({ page: next })}
          sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}
        />
      ) : null}
    </Box>
  )
}
